import React from "react";

/* ------------------------------------------------------------------ */
/*  Sparkline — lightweight inline SVG trend line                     */
/* ------------------------------------------------------------------ */

export interface SparklineProps {
  /** Array of numeric data points. */
  data: number[];
  /** Width in pixels (default 80). */
  width?: number;
  /** Height in pixels (default 32). */
  height?: number;
  /** Stroke color (default: --sf-primary). */
  color?: string;
  /** Fill gradient opacity (default 0.08). Set to 0 for line-only. */
  fillOpacity?: number;
  /** Additional class name. */
  className?: string;
}

const Sparkline = React.forwardRef<SVGSVGElement, SparklineProps>(
  ({ data, width = 80, height = 32, color, fillOpacity = 0.08, className, ...rest }, ref) => {
    if (!data || data.length < 2) return null;

    const strokeColor = color || "var(--sf-primary, #0ca678)";
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 2;
    const effectiveHeight = height - padding * 2;
    const step = (width - padding * 2) / (data.length - 1);

    const points = data.map(
      (v, i) =>
        `${padding + i * step},${padding + effectiveHeight - ((v - min) / range) * effectiveHeight}`,
    );
    const polylinePoints = points.join(" ");

    // Fill area: same points + bottom-right + bottom-left
    const fillPoints = [
      ...points,
      `${padding + (data.length - 1) * step},${height}`,
      `${padding},${height}`,
    ].join(" ");

    const gradientId = `sf-sparkline-grad-${Math.random().toString(36).slice(2, 8)}`;

    return (
      <svg
        ref={ref}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        fill="none"
        className={className}
        style={{ display: "block", flexShrink: 0 }}
        {...rest}
      >
        {fillOpacity > 0 && (
          <>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokeColor} stopOpacity={fillOpacity * 2} />
                <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <polygon points={fillPoints} fill={`url(#${gradientId})`} />
          </>
        )}
        <polyline
          points={polylinePoints}
          stroke={strokeColor}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);

Sparkline.displayName = "Sparkline";
export { Sparkline };
